/**
 * Request Cart / B2B Anfrage System
 * 
 * Manages product inquiry list with LocalStorage persistence,
 * drawer UI, and form submission via Shopify contact form.
 */

const RequestCart = {
  STORAGE_KEY: 'seka_request_cart',
  items: [],
  drawer: null,
  toast: null,
  
  /**
   * Initialize the request cart system
   */
  init() {
    this.drawer = document.querySelector('[data-request-drawer]');
    this.toast = document.querySelector('[data-request-toast]');
    
    if (!this.drawer) return;
    
    this.loadFromStorage();
    this.bindEvents();
    this.updateUI();
  },
  
  // ==========================================================================
  // LocalStorage Management
  // ==========================================================================
  
  loadFromStorage() {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      this.items = stored ? JSON.parse(stored) : [];
    } catch (e) {
      console.warn('RequestCart: Could not load from localStorage', e);
      this.items = [];
    }
  },
  
  saveToStorage() {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(this.items));
    } catch (e) {
      console.warn('RequestCart: Could not save to localStorage', e);
    }
  },
  
  clearStorage() {
    try {
      localStorage.removeItem(this.STORAGE_KEY);
      this.items = [];
    } catch (e) {
      console.warn('RequestCart: Could not clear localStorage', e);
    }
  },
  
  // ==========================================================================
  // Item Management
  // ==========================================================================
  
  addItem(product) {
    const existing = this.items.find(i => 
      i.product_id === product.product_id && 
      i.variant_id === product.variant_id
    );
    
    if (existing) {
      existing.quantity += product.quantity || 1;
    } else {
      this.items.push({
        product_id: product.product_id,
        variant_id: product.variant_id,
        title: product.title,
        variant_title: product.variant_title || '',
        image_url: product.image_url,
        product_url: product.product_url,
        quantity: product.quantity || 1
      });
    }
    
    this.saveToStorage();
    this.updateUI();
    this.open();
    this.showToast(product.title + ' zur Anfrage hinzugefügt');
  },
  
  updateQuantity(productId, variantId, change) {
    const item = this.items.find(i => 
      i.product_id == productId && 
      i.variant_id == variantId
    );
    
    if (item) {
      item.quantity = Math.max(1, item.quantity + change);
      this.saveToStorage();
      this.updateUI();
    }
  },
  
  removeItem(productId, variantId) {
    this.items = this.items.filter(i => 
      !(i.product_id == productId && i.variant_id == variantId)
    );
    this.saveToStorage();
    this.updateUI();
    this.showToast('Produkt entfernt');
  },
  
  getItemCount() {
    return this.items.reduce((sum, item) => sum + item.quantity, 0);
  },
  
  // ==========================================================================
  // Drawer Control
  // ==========================================================================
  
  open() {
    if (!this.drawer) return;
    this.drawer.classList.add('is-open');
    this.drawer.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
  },
  
  close() {
    if (!this.drawer) return;
    this.drawer.classList.remove('is-open');
    this.drawer.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    this.hideForm();
  },
  
  toggle() {
    if (this.drawer && this.drawer.classList.contains('is-open')) {
      this.close();
    } else {
      this.open();
    }
  },
  
  // ==========================================================================
  // UI Updates
  // ==========================================================================
  
  updateUI() {
    this.updateCount();
    this.renderItems();
    this.toggleEmptyState();
    this.updateFormSummary();
  },
  
  updateCount() {
    const countElements = document.querySelectorAll('[data-request-count]');
    const count = this.getItemCount();
    
    countElements.forEach(el => {
      el.textContent = count;
      el.style.display = count > 0 ? '' : 'none';
    });
  },
  
  toggleEmptyState() {
    if (!this.drawer) return;
    
    if (this.items.length === 0) {
      this.drawer.classList.add('is-empty');
    } else {
      this.drawer.classList.remove('is-empty');
    }
  },
  
  renderItems() {
    const container = document.querySelector('[data-request-items]');
    if (!container) return;
    
    if (this.items.length === 0) {
      container.innerHTML = '';
      return;
    }
    
    container.innerHTML = this.items.map(item => this.renderItemHTML(item)).join('');
  },
  
  renderItemHTML(item) {
    return `
      <div class="request-drawer-item" data-request-item data-product-id="${item.product_id}" data-variant-id="${item.variant_id}">
        <div class="request-drawer-item__image">
          <a href="${item.product_url}">
            <img src="${item.image_url}" alt="${this.escapeHtml(item.title)}" loading="lazy">
          </a>
        </div>
        <div class="request-drawer-item__details">
          <a href="${item.product_url}" class="request-drawer-item__title" data-anfrage-produkt>${this.escapeHtml(item.title)}</a>
          ${item.variant_title ? `<p class="request-drawer-item__variant">${this.escapeHtml(item.variant_title)}</p>` : ''}
          <span class="request-drawer-item__badge">Preis auf Anfrage</span>
        </div>
        <div class="request-drawer-item__quantity">
          <button type="button" class="request-drawer-item__qty-btn" data-action="decrease" aria-label="Menge verringern">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="5" y1="12" x2="19" y2="12"></line>
            </svg>
          </button>
          <input type="number" class="request-drawer-item__qty-input" value="${item.quantity}" min="1" data-quantity-input readonly>
          <button type="button" class="request-drawer-item__qty-btn" data-action="increase" aria-label="Menge erhöhen">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="12" y1="5" x2="12" y2="19"></line>
              <line x1="5" y1="12" x2="19" y2="12"></line>
            </svg>
          </button>
        </div>
        <button type="button" class="request-drawer-item__remove" data-action="remove" aria-label="Entfernen">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
      </div>
    `;
  },
  
  // ==========================================================================
  // Form Handling
  // ==========================================================================
  
  showForm() {
    const formView = document.querySelector('[data-request-form-view]');
    if (formView) {
      formView.classList.add('is-visible');
      this.updateFormSummary();
    }
  },
  
  hideForm() {
    const formView = document.querySelector('[data-request-form-view]');
    if (formView) {
      formView.classList.remove('is-visible');
    }
  },
  
  updateFormSummary() {
    const summaryList = document.querySelector('[data-request-summary-list]');
    const itemsInput = document.querySelector('[data-request-items-input]');
    
    if (summaryList) {
      summaryList.innerHTML = this.items.map(item => `
        <div class="request-form__summary-item">
          <span>${this.escapeHtml(item.title)}${item.variant_title ? ` - ${this.escapeHtml(item.variant_title)}` : ''}</span>
          <span>× ${item.quantity}</span>
        </div>
      `).join('');
    }
    
    // Prepare formatted text for email
    if (itemsInput) {
      const formattedItems = this.items.map(item => 
        `• ${item.title}${item.variant_title ? ` (${item.variant_title})` : ''} - Menge: ${item.quantity} - ${window.location.origin}${item.product_url}`
      ).join('\n');
      
      itemsInput.value = formattedItems;
    }
  },
  
  handleFormSubmit(form) {
    if (this.items.length === 0) {
      this.showToast('Keine Produkte in der Anfrage', true);
      return false;
    }
    
    // Update the body field with product list
    const bodyField = form.querySelector('[name="contact[body]"]');
    const itemsInput = form.querySelector('[data-request-items-input]');
    
    if (bodyField && itemsInput) {
      const originalMessage = bodyField.value;
      const productList = itemsInput.value;
      
      bodyField.value = `ANGEFRAGTE PRODUKTE:\n${productList}\n\n` + 
                        (originalMessage ? `NACHRICHT:\n${originalMessage}` : '');
    }
    
    // Clear cart after successful submit (form will reload page)
    this.clearStorage();
    
    return true;
  },
  
  // ==========================================================================
  // Event Binding
  // ==========================================================================
  
  bindEvents() {
    // Add to request buttons (on product pages and cards)
    document.addEventListener('click', (e) => {
      const addBtn = e.target.closest('[data-add-to-request]');
      if (addBtn) {
        e.preventDefault();
        this.handleAddToRequest(addBtn);
      }
    });
    
    // Drawer close buttons
    document.addEventListener('click', (e) => {
      if (e.target.closest('[data-request-drawer-close]')) {
        this.close();
      }
    });
    
    // Show form button
    document.addEventListener('click', (e) => {
      if (e.target.closest('[data-request-show-form]')) {
        this.showForm();
      }
    });
    
    // Back to list button
    document.addEventListener('click', (e) => {
      if (e.target.closest('[data-request-back-to-list]')) {
        this.hideForm();
      }
    });
    
    // Item actions (quantity, remove)
    document.addEventListener('click', (e) => {
      const item = e.target.closest('[data-request-item]');
      if (!item) return;
      
      const productId = item.dataset.productId;
      const variantId = item.dataset.variantId;
      const action = e.target.closest('[data-action]')?.dataset.action;
      
      if (action === 'increase') {
        this.updateQuantity(productId, variantId, 1);
      } else if (action === 'decrease') {
        this.updateQuantity(productId, variantId, -1);
      } else if (action === 'remove') {
        this.removeItem(productId, variantId);
      }
    });
    
    // Form submission
    const form = document.querySelector('#request-contact-form');
    if (form) {
      form.addEventListener('submit', (e) => {
        if (!this.handleFormSubmit(form)) {
          e.preventDefault();
        }
      });
    }
    
    // Escape key to close
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.drawer?.classList.contains('is-open')) {
        this.close();
      }
    });
    
    // Toggle button (if exists in header)
    document.addEventListener('click', (e) => {
      if (e.target.closest('[data-request-drawer-toggle]')) {
        this.toggle();
      }
    });
  },
  
  handleAddToRequest(button) {
    const product = {
      product_id: button.dataset.productId,
      variant_id: button.dataset.variantId,
      title: button.dataset.title,
      variant_title: button.dataset.variantTitle || '',
      image_url: button.dataset.image,
      product_url: button.dataset.url,
      quantity: parseInt(button.dataset.quantity) || 1
    };
    
    this.addItem(product);
  },
  
  // ==========================================================================
  // Toast Notifications
  // ==========================================================================
  
  showToast(message, isError = false) {
    if (!this.toast) return;
    
    const messageEl = this.toast.querySelector('[data-request-toast-message]');
    if (messageEl) {
      messageEl.textContent = message;
    }
    
    this.toast.classList.remove('is-error');
    if (isError) {
      this.toast.classList.add('is-error');
    }
    
    this.toast.classList.add('is-visible');
    
    clearTimeout(this.toastTimeout);
    this.toastTimeout = setTimeout(() => {
      this.toast.classList.remove('is-visible');
    }, 3000);
  },
  
  // ==========================================================================
  // Utility
  // ==========================================================================
  
  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
};

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => RequestCart.init());
} else {
  RequestCart.init();
}

// Expose globally for external use
window.RequestCart = RequestCart;
